const EntryService = require('../services/EntryService').default;
const getSpace = require('./contentful/get-space');
const getEntryCollection = require('./contentful/entry-collection-get');
const transformer = require('./transformers/makeImage');
const publishEntry = require('./contentful/entry-publish');
const linkResolve = require('./contentful/link-resolve');
const processError = require('./contentful/error-process');
const entryArchive = require('./contentful/entry-archive');
const entryCollectionSync = require('./contentful/entry-collection-sync');

let entryService = null;

const gettingEntryService = () => {
  if (!entryService) {
    entryService = getSpace().then((space) => {
      return new EntryService(space, transformer);
    });
  }

  return entryService;
};

const internalSaveCollection = async (space, makeImages) => {
  const data = makeImages.map(transformer.internalToContentful);
  const entries = await entryCollectionSync(space, data);

  // Resolve the underlying data because otherwise the asset on the make image is not resolved.
  const resolvedData = await linkResolve(space, entries, 2);

  return resolvedData.map(transformer.contentfulToInternal);
};

module.exports = {
  getById(...parameters) {
    return gettingEntryService().then((service) => service.getById(...parameters));
  },

  save(...parameters) {
    return gettingEntryService().then((service) => service.save(...parameters));
  },

  create(...parameters) {
    return gettingEntryService().then((service) => service.create('makeImage', ...parameters));
  },

  publish(...parameters) {
    return gettingEntryService().then((service) => service.publish(...parameters));
  },

  unpublish(...parameters) {
    return gettingEntryService().then((service) => service.unpublish(...parameters));
  },

  archive(...parameters) {
    return gettingEntryService().then((service) => service.archive(...parameters));
  },

  unarchive(...parameters) {
    return gettingEntryService().then((service) => service.unarchive(...parameters));
  },

  initializeCustom(...parameters) {
    return gettingEntryService().then((service) => service.initializeCustom('makeImage', ...parameters));
  },

  getByModelId(modelId, depth = 0) {
    if (!modelId) {
      throw new Error('Expected non-falsy model ID.');
    }

    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'makeImage',
        'fields.model.sys.id': modelId,
        'order': 'fields.priority,-sys.updatedAt'
      }).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  saveCollection(space, originalMakeImages, currentMakeImages) {
    return internalSaveCollection(space, currentMakeImages).then((makeImages) => {
      const removedMakeImages = originalMakeImages.filter((first) => !makeImages.some((second) => first.sys.id === second.id));
      if (removedMakeImages.length === 0) {
        return makeImages;
      }

      return getEntryCollection(space, removedMakeImages.map((makeImage) => makeImage.sys.id)).then((collection) => {
        return Promise.all(collection.items.map((entry) => {
          return entryArchive(entry).catch(processError);
        }));
      }).then(() => {
        return makeImages;
      });
    });
  },

  publishCollection(ids) {
    // Exit out fast if we have no IDs to publish
    if (ids.length === 0) {
      return Promise.resolve();
    }

    return getSpace().then((space) => {
      return getEntryCollection(space, ids).then((response) => {
        return Promise.all(response.items.map(publishEntry));
      });
    });
  }
};
